import { IContextualMenuItem, Spinner, SpinnerSize } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import authModule from "../../../utilities/AuthModule";
import { customToast } from "../../../src/taskpane/components/CustomToast";
import { apiScanTemplateCustomFields } from "../../../src/taskpane/services/ApiService";
import { refreshDatasourceDocumentTemplates } from "../../../src/taskpane/services/DatasourceTemplateService";
import { isTemplateDatasourceLocation } from "../../../src/taskpane/components/helpers/customFieldsHelper";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";
import { signalCacheUpdate } from "../../../src/taskpane/redux/modules/cacheUpdate";
import { TemplateScanResult } from "../../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { FolderSearch20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FolderSearch20Regular";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { isSupportedTemplateExtension, scanCustomFieldsExtensions } from "../../../src/taskpane/config/constants";

// The grid's scoped ToastContainer (ContentBodyFilesFolders): menu actions fire outside any
// panel, and react-toastify only queues toasts invisibly until some container mounts.
export const CM_BODY_TOAST_CONTAINER_ID = "cmBody";
const bodyToast = { containerId: CM_BODY_TOAST_CONTAINER_ID };

export function useMenuItem_ScanCustomFields(
  selectedItems: any[],
  waitFor: boolean,
  // Persistent surface (ScanErrorsMessageBar in the content body). Errors AND skipped
  // files are actionable - a skipped template will never expose custom fields until its
  // author acts - so both must outlive a toast. Falls back to an error toast when the
  // host provides no callback.
  onScanErrors?: (errors: string[], skipped?: string[]) => void,
) {
  const languageStrings = React.useContext(LanguageContext);
  const dispatch = useDispatch();

  const datasources = useSelector((state: RootState) => state.tenantInfo?.datasources);
  const userAccount = useSelector((state: RootState) => state.user?.account);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));

  const [isScanning, setIsScanning] = React.useState(false);
  // Ref-based re-entrancy guard: the memoized `disabled` flag only takes effect after a
  // re-render, so a second activation racing the first render-cycle must be caught here.
  const isScanningRef = React.useRef(false);

  const menuItem_ScanCustomFields = React.useMemo(() => {
    const templateDatasource = isTemplateDatasourceLocation(
      datasources,
      currentDirectory?.webUrl,
      currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList);

    if (!templateDatasource) return null;

    const permissionState = getFolderPermissionMenuState({
      currentSharepointFolder,
      requiredPermissions: ["canEditListItem"],
    });
    if (!permissionState.shouldRender) return null;

    // Selection-aware: selected files => scan just those; none => scan the whole library.
    const selectedFiles = (selectedItems ?? [])
      .filter(item => item?.FSObjType !== "1" && item?.FileSystemObjectType !== 1);
    const selectedFileIds: string[] = selectedFiles
      .map(item => (item?.UniqueId ?? item?.uniqueid ?? "").replace("{", "").replace("}", ""))
      .filter(id => id);

    // Hidden only when the user has picked files and NOT ONE of them can be read.
    //
    // Three things this must not break, which is why it is not a simple per-file test:
    //  - NO selection means a whole-library scan, so an empty selection can never hide this;
    //  - a MIXED selection still renders - the readable files are exactly what the command is
    //    for, and the rest are handled below;
    //  - a folders-only selection still renders, so the click can say so (ScanCustomFieldsNoFiles)
    //    rather than the command silently vanishing.
    //
    // Known consequence: for an all-unreadable selection the server's deliberate no-op - writing
    // an EMPTY envelope so stale custom-fields JSON cannot linger on a file that no longer has
    // any - is no longer reachable from that selection. It still runs in a whole-library scan,
    // which is the only place it mattered.
    const canScanSomething = selectedFiles.some(item =>
      isSupportedTemplateExtension(item?.FileLeafRef ?? item?.name ?? "", scanCustomFieldsExtensions));
    if (selectedFiles.length > 0 && !canScanSomething) return null;

    return {
      key: "scanCustomFields",
      disabled: permissionState.isDisabled || waitFor || isScanning || !currentDirectory?.sharePointListId,
      iconProps: {},
      onRenderIcon: () => <FolderSearch20Regular className={menuItemIconStyles} />,
      text: languageStrings.ScanCustomFields,
      onClick: () => {
        runFolderPermissionMenuAction(permissionState, async () => {
          if (isScanningRef.current) return;

          // A selection that contains no files (folders only) must not silently widen
          // into a whole-library scan - tell the user instead.
          if ((selectedItems?.length ?? 0) > 0 && selectedFileIds.length === 0) {
            customToast.info(languageStrings.ScanCustomFieldsNoFiles, bodyToast);
            return;
          }

          isScanningRef.current = true;
          setIsScanning(true);
          // A new scan is starting: clear the previous scan's bar NOW, so it can never be
          // misread as describing this run.
          onScanErrors?.([], []);
          const reportToBar = (errors: string[], skipped: string[] = []) => {
            if (onScanErrors) {
              onScanErrors(errors, skipped);
            } else if (errors.length > 0 || skipped.length > 0) {
              customToast.error(`${languageStrings.ScanCustomFieldsFailed}: ${[...errors, ...skipped].join("; ")}`, bodyToast);
            }
          };
          try {
            const response = await apiScanTemplateCustomFields({
              accessToken: await authModule.getSharePointAccessToken(),
              tenantGuid: userAccount?.tenantId,
              accountId: userAccount?.accountId,
              siteUrl: currentDirectory?.webUrl,
              listId: currentDirectory?.sharePointListId,
              templateUniqueIds: selectedFileIds.length > 0 ? selectedFileIds : undefined,
            });

            if (response?.isSuccess === false || !Array.isArray(response?.value)) {
              reportToBar([response?.error ?? languageStrings.ErrorMessageGeneric]);
              return;
            }

            const results: TemplateScanResult[] = response.value;
            const errors = results.filter(r => r.status === "error");
            const skipped = results.filter(r => r.status === "skipped");
            const withFields = results.filter(r => r.fields?.length > 0).length;

            // Errors and skips go to the persistent bar, one line per file - both are
            // actionable and must not vanish with a toast.
            reportToBar(
              errors.map(e => `${e.fileName}: ${e.errors?.[0] ?? "?"}`),
              skipped.map(s => `${s.fileName}: ${s.errors?.[0] ?? "?"}`));

            // The transient toast answers the user's actual question - "did my templates
            // get their custom fields?" - not just how many items were written. Neutral
            // (info) when the persistent bar is simultaneously showing errors: a green
            // "success" next to a red error bar reads as a contradiction.
            const summary = `${results.length} ${languageStrings.ScanCustomFieldsScanned} — ${withFields} ${languageStrings.ScanCustomFieldsWithFields}`
              + (skipped.length > 0 ? `, ${skipped.length} ${languageStrings.ScanCustomFieldsSkipped}` : "");
            if (errors.length === 0) {
              customToast.success(summary, bodyToast);
            } else {
              customToast.info(summary, bodyToast);
            }

            if (results.some(r => r.status === "updated") && (await refreshDatasourceDocumentTemplates(datasources))) {
              dispatch(signalCacheUpdate());
            }
          } catch (error: any) {
            reportToBar([error?.message ?? languageStrings.ErrorMessageGeneric]);
          } finally {
            isScanningRef.current = false;
            setIsScanning(false);
          }
        });
      },
    } as IContextualMenuItem;
  }, [datasources, currentDirectory, currentSharepointFolder, selectedItems, waitFor, isScanning, languageStrings, userAccount, dispatch, onScanErrors]);

  // Visibility of system status: a whole-library scan does paged enumeration, throttled
  // downloads and per-item writes - easily half a minute. A disabled menu item alone reads
  // as broken; this is the same command-bar status idiom as loadingFiles/savingFiles, and
  // its disappearance doubles as the "done" cue for the summary toast.
  const menuItem_ScanningTemplates = React.useMemo(() => {
    if (!isScanning) return null;
    return {
      key: "scanningTemplates",
      text: languageStrings.ScanCustomFieldsScanning,
      disabled: true,
      onRenderIcon: () => <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />,
    } as IContextualMenuItem;
  }, [isScanning, languageStrings]);

  return {
    menuItem_ScanCustomFields,
    menuItem_ScanningTemplates
  };
}
